import { useMemo } from 'react';
import { match, P } from 'ts-pattern';
import { makePath } from 'utils/routes';

import type { ActivePath } from './ActivePaths';
import type { NavTree } from './SideNav';
import { useIntegrationsNavTree } from './useIntegrationsNavTree';
import { useProjectNav } from './useProjectsNavTree';

export const makeSchemaLedgerHomePath = ({
  workspaceId,
  schemaKey,
  ledgerId,
}: {
  workspaceId: string;
  schemaKey: string;
  ledgerId: string;
}) =>
  makePath(
    ['w', workspaceId, 'projects', 'schema', schemaKey, ledgerId, 'home'],
    {
      absolute: true,
    }
  );

export const makeNonSchemaLedgerHomePath = ({
  workspaceId,
  ledgerId,
}: {
  workspaceId: string;
  ledgerId: string;
}) =>
  makePath(['w', workspaceId, 'projects', ledgerId, 'home'], {
    absolute: true,
  });

export const makeLedgerHomePath = ({
  ledgerId,
  schemaKey,
  workspaceId,
}: {
  ledgerId: string;
  schemaKey?: string;
  workspaceId: string;
}) =>
  schemaKey
    ? makeSchemaLedgerHomePath({ ledgerId, schemaKey, workspaceId })
    : makeNonSchemaLedgerHomePath({ ledgerId, workspaceId });

export const makeEntryPath = ({
  workspaceId,
  ledgerId,
  entryId,
  schemaKey,
}: {
  workspaceId: string;
  ledgerId: string;
  entryId: string;
  schemaKey?: string;
}) =>
  match([schemaKey, ledgerId])
    // Schemad Ledger
    .with([P.string, P.string], ([sk, lid]) =>
      makePath(
        [
          'w',
          workspaceId,
          'projects',
          'schema',
          sk,
          lid,
          'entries',
          entryId,
          'lines',
        ],
        {
          absolute: true,
        }
      )
    )
    // Non-schemad Ledger
    .with([P.nullish, P.string], ([_unused, lid]) =>
      makePath(
        ['w', workspaceId, 'projects', lid, 'entries', entryId, 'lines'],
        {
          absolute: true,
        }
      )
    )
    // We're screwed.
    .otherwise(() => {
      throw new Error(`Could not determine path for Ledger Entry: ${entryId}`);
    });

export const makeTxPath = ({
  workspaceId,
  linkId,
  externalAccountId,
  txId,
}: {
  workspaceId: string;
  linkId: string;
  externalAccountId: string;
  txId: string;
}) =>
  makePath(
    [
      'w',
      workspaceId,
      'integrations',
      'links',
      'id',
      linkId,
      'accounts',
      externalAccountId,
      'tx',
      txId,
    ],
    {
      absolute: true,
    }
  );

export const makeExternalAccountPath = ({
  workspaceId,
  linkId,
  externalAccountId,
}: {
  workspaceId: string;
  linkId: string;
  externalAccountId: string;
}) =>
  makePath(
    [
      'w',
      workspaceId,
      'integrations',
      'links',
      'id',
      linkId,
      'accounts',
      externalAccountId,
    ],
    {
      absolute: true,
    }
  );

export const makeLinkPath = ({
  workspaceId,
  linkId,
}: {
  workspaceId: string;
  linkId: string;
}) =>
  makePath(['w', workspaceId, 'integrations', 'links', 'id', linkId], {
    absolute: true,
  });
export const makeLedgerAccountPath = ({
  workspaceId,
  schemaKey,
  ledgerId,
  accountId,
}: {
  workspaceId: string;
  ledgerId: string;
  schemaKey?: string;
  accountId: string;
}) =>
  match([schemaKey, ledgerId])
    // Schemad Ledger
    .with([P.string, P.string], ([_schemaKey, _ledgerId]) =>
      makePath(
        [
          'w',
          workspaceId,
          'projects',
          'schema',
          _schemaKey,
          _ledgerId,
          'accounts',
          accountId,
        ],
        {
          absolute: true,
        }
      )
    )
    // Non-schemad Ledger
    .with([P.nullish, P.string], ([_unused, _ledgerId]) =>
      makePath(
        ['w', workspaceId, 'projects', _ledgerId, 'accounts', accountId],
        {
          absolute: true,
        }
      )
    )
    .otherwise(() => {
      throw new Error(`Could not determine path for Account: ${accountId}`);
    });

export const makeLinePath = ({
  workspaceId,
  schemaKey,
  ledgerId,
  ledgerEntryId,
  lineId,
}: {
  workspaceId: string;
  ledgerId: string;
  schemaKey?: string;
  ledgerEntryId: string;
  lineId: string;
}) =>
  match([schemaKey, lineId])
    // Non-schemad Ledger
    .with([P.nullish, P.nonNullable], ([, _lineId]) =>
      makePath(
        [
          'w',
          workspaceId,
          'projects',
          ledgerId,
          'entries',
          ledgerEntryId,
          'lines',
          _lineId,
        ],
        { absolute: true }
      )
    )
    .with([P.string, P.nonNullable], ([_schemaKey, _lineId]) =>
      // Schemad Ledger
      makePath(
        [
          'w',
          workspaceId,
          'projects',
          'schema',
          _schemaKey,
          ledgerId,
          'entries',
          ledgerEntryId,
          'lines',
          _lineId,
        ],
        { absolute: true }
      )
    )
    .otherwise(() => {
      throw new Error(`Could not determine path for Line: ${lineId}`);
    });

export type DashboardNavTreeProps = {
  active: ActivePath;
  workspaceId: string;
  ledgerId?: string;
  accountId?: string;
  linkId?: string;
  groupId?: string;
  externalAccountId?: string;
  schemaKey?: string;
};

export const useDashboardNavTree = ({
  active,
  workspaceId,
  ledgerId,
  accountId,
  linkId,
  externalAccountId,
  groupId,
  schemaKey,
}: DashboardNavTreeProps): NavTree => {
  const integrationsNavTree = useIntegrationsNavTree({
    active,
    workspaceId,
    linkId,
    externalAccountId,
  });
  const projectsNavTree = useProjectNav({
    active,
    workspaceId,
    schemaKey,
    ledgerId,
    accountId,
    groupId,
  });
  return useMemo(
    () => ({
      ...projectsNavTree,
      ...integrationsNavTree,
    }),
    [projectsNavTree, integrationsNavTree]
  );
};
